import { type EnhancedDbClient } from '@erikdakoda/database/server/getEnhancedDbClient';
import type { SettingValue, SettingValueCreateArgs } from '@erikdakoda/database/types';
import type { SettingType } from '@erikdakoda/database/enums';
import type { SettingValueForRead } from '../types';
import { getCurrentAppId } from '@erikdakoda/utils/getCurrentAppId';

type SettingLookupOptions = {
  group?: string | null;
  userId?: string;
};

/**
 * Write a setting value for a user (creates or replaces by owner/group/key/app).
 * Pass `group` when overriding a SettingTemplate so the composite FK resolves.
 * Untemplated rows (caches, tokens) omit `group` so it stays null.
 */
export async function writeSetting(
  db: EnhancedDbClient,
  key: string,
  value: string | number | boolean | object | Date,
  group?: string | null,
  userId?: string,
): Promise<SettingValue> {
  const type = getSettingType(value);

  if (!type) {
    throw new Error(`Invalid value type: ${typeof value}`);
  }

  if (!userId) {
    userId = process.env.ADMIN_USER_ID!;
  }

  const app = getCurrentAppId();
  const valueString = encodeSettingValue(value);

  await deleteSetting(db, key, { group, userId });

  const data = {
    key,
    type,
    value: valueString,
    ...(group != null ? { group } : {}),
    app,
    ownerId: userId,
  } satisfies SettingValueCreateArgs['data'];

  const setting = await db.settingValue.create({ data });

  return setting;
}

/**
 * Converts a SettingValue's string value to its proper type based on the setting's type.
 * @param setting The SettingValue object to convert
 * @returns The converted value
 */
export function decodeSettingValue<T = SettingType>(setting: SettingValueForRead): T {
  const type = setting.type;
  const value =
    type === 'string'
      ? setting.value
      : type === 'date'
        ? new Date(setting.value)
        : type === 'number'
          ? Number(setting.value)
          : type === 'boolean'
            ? JSON.parse(setting.value)
            : type === 'object'
              ? JSON.parse(setting.value)
              : setting.value;

  return value as T;
}

export function encodeSettingValue(value: unknown): string {
  return typeof value === 'string'
    ? value
    : value instanceof Date
      ? value.toISOString()
      : JSON.stringify(value);
}

export function getSettingType(value: unknown): SettingType | undefined {
  return typeof value === 'string'
    ? 'string'
    : value instanceof Date
      ? 'date'
      : typeof value === 'number'
        ? 'number'
        : typeof value === 'boolean'
          ? 'boolean'
          : typeof value === 'object'
            ? 'object'
            : undefined;
}

/**
 * Builds a where clause fragment for optional group matching.
 * `undefined` omits the filter; `null` matches SQL NULL via `{ equals: null }`.
 */
function groupWhere(group: string | null | undefined): { group?: string | { equals: null } } {
  if (group === undefined) {
    return {};
  }
  if (group === null) {
    return { group: { equals: null } };
  }
  return { group };
}

/**
 * Read a setting value for a user by key (and optional group).
 */
export async function readSetting<T = SettingType>(
  db: EnhancedDbClient,
  key: string,
  options?: SettingLookupOptions,
): Promise<T | null> {
  const userId = options?.userId ?? process.env.ADMIN_USER_ID!;
  const group = options?.group;

  const setting = await db.settingValue.findFirst({
    where: {
      ownerId: userId,
      key,
      app: getCurrentAppId(),
      ...groupWhere(group),
    },
  });

  if (!setting) return null;

  return decodeSettingValue<T>(setting);
}

/**
 * Reads a setting and returns both the SettingValue object (with metadata like updatedAt) and the converted value.
 * @param db Database instance
 * @param key The unique identifier for the setting to retrieve
 * @param options Optional group and userId
 * @returns Object containing the SettingValue and the converted value, or null if not found
 */
export async function readSettingWithMeta<T = SettingType>(
  db: EnhancedDbClient,
  key: string,
  options?: SettingLookupOptions,
): Promise<{ setting: SettingValue; value: T } | null> {
  const userId = options?.userId ?? process.env.ADMIN_USER_ID!;
  const group = options?.group;

  const setting = await db.settingValue.findFirst({
    where: {
      ownerId: userId,
      key,
      app: getCurrentAppId(),
      ...groupWhere(group),
    },
  });

  if (!setting) return null;

  return { setting, value: decodeSettingValue<T>(setting) };
}

/**
 * Delete a setting for a user by key (and optional group).
 * @returns true if a row was deleted
 */
export async function deleteSetting(
  db: EnhancedDbClient,
  key: string,
  options?: SettingLookupOptions,
): Promise<boolean> {
  const userId = options?.userId ?? process.env.ADMIN_USER_ID!;
  const group = options?.group;

  const setting = await db.settingValue.findFirst({
    where: {
      ownerId: userId,
      key,
      app: getCurrentAppId(),
      ...groupWhere(group),
    },
    select: { id: true },
  });

  if (!setting) return false;

  await db.settingValue.delete({
    where: { id: setting.id },
  });

  return true;
}
